import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Plus, UploadCloud, FileUp, FileText, X, Trash2, ShieldCheck } from 'lucide-react';
import { useAssetStore, type Asset } from '../../../store/assetStore';
import { useAssetCatalogStore } from '../../../store/assetCatalogStore';
import { useEmployeeAssetsStore, type AssetEmployee } from '../../../store/employeeAssetsStore';
import { EmployeeCard } from '../../../components/assets/EmployeeAssets';
import { processSelectedFiles, UploadedFileItem } from '../../../utils/fileUpload';
import { cn } from '../../../lib/utils';
import toast from 'react-hot-toast';

export function AddAssetView() {
  const navigate = useNavigate();
  const { id: editId } = useParams<{ id: string }>();
  const isEdit = Boolean(editId);
  const { createAsset, updateAsset, fetchAsset, nextAssetCode, assignAsset, addAttachment } = useAssetStore();
  const { vendors, categories, types, fetchVendors, fetchCategories, fetchTypes } = useAssetCatalogStore();
  const { fetchDirectory } = useEmployeeAssetsStore();
  const [employees, setEmployees] = useState<(AssetEmployee & { department: string })[]>([]);
  const [assignEmployeeId, setAssignEmployeeId] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const SECTIONS = ['general', 'purchase', 'assignment', 'specs', 'media', 'notes'] as const;
  type SectionId = typeof SECTIONS[number];
  const [activeSection, setActiveSection] = useState<SectionId>('general');

  const currentStep = SECTIONS.indexOf(activeSection);
  const isLastStep = currentStep === SECTIONS.length - 1;
  const goToStep = (index: number) => {
    const clamped = Math.max(0, Math.min(SECTIONS.length - 1, index));
    setActiveSection(SECTIONS[clamped]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Guard against an accidental double-click submitting on arrival at the last step:
  // the Register button only becomes active a moment after the final step is shown,
  // so a second "Next" click can't land on Register and submit.
  const [readyToSubmit, setReadyToSubmit] = useState(false);
  useEffect(() => {
    if (!isLastStep) { setReadyToSubmit(false); return; }
    setReadyToSubmit(false);
    const t = setTimeout(() => setReadyToSubmit(true), 500);
    return () => clearTimeout(t);
  }, [isLastStep]);

  const [uploadedPhotos, setUploadedPhotos] = useState<UploadedFileItem[]>([]);
  const [uploadedInvoiceDoc, setUploadedInvoiceDoc] = useState<UploadedFileItem | null>(null);
  const [uploadedWarrantyDoc, setUploadedWarrantyDoc] = useState<UploadedFileItem | null>(null);

  const [isDraggingPhotos, setIsDraggingPhotos] = useState(false);
  const [isDraggingInvoice, setIsDraggingInvoice] = useState(false);
  const [isDraggingWarranty, setIsDraggingWarranty] = useState(false);

  const photoFileInputRef = useRef<HTMLInputElement>(null);
  const invoiceFileInputRef = useRef<HTMLInputElement>(null);
  const warrantyFileInputRef = useRef<HTMLInputElement>(null);

  /** Form values; empty means "not recorded" — nothing is pre-filled with demo data. */
  const [form, setForm] = useState<Record<string, any>>({
    asset_code: '',
    name: '',
    type: '',
    category: '',
    manufacturer: '',
    model: '',
    serial_number: '',
    ownership: 'owned',
    acquisitionType: 'purchase',
    supplierId: '',
    purchaseCost: '',
    purchaseDate: '',
    poNumber: '',
    invoiceNumber: '',
    salvageValue: '',
    warrantyStart: '',
    warrantyUntil: '',
    department: '',
    location: '',
    os: '',
    ram: '',
    storage: '',
    cpuSpeed: '',
    cpuCoreCount: '',
    macAddress: '',
    ipAddress: '',
    notes: '',
  });

  /** Fields without their own column are kept in assets.specifications. */
  const SPEC_FIELDS = ['acquisitionType', 'salvageValue', 'cpuCoreCount'];

  useEffect(() => {
    fetchVendors();
    fetchCategories();
    fetchTypes();
    // Employees from people.employees via the assets service (no People permission needed).
    fetchDirectory()
      .then((rows) => setEmployees(rows.map((e) => ({ ...e, department: e.department || '' }))))
      .catch(() => undefined);
  }, [fetchVendors, fetchCategories, fetchTypes, fetchDirectory]);

  useEffect(() => {
    if (!form.type && types.length > 0) {
      const preferred = types.find((t) => t.name.toLowerCase() === 'hardware') || types[0];
      setForm((prev) => (prev.type ? prev : { ...prev, type: preferred.name }));
    }
  }, [types, form.type]);

  // Edit: load the real asset. Create: ask the server for the next asset code.
  useEffect(() => {
    if (editId) {
      fetchAsset(editId)
        .then((a: Asset) => setForm((prev) => ({
          ...prev,
          asset_code: a.asset_code, name: a.name, type: a.type || 'Hardware', category: a.category || '',
          manufacturer: a.manufacturer || '', model: a.model || '', serial_number: a.serial_number || '',
          ownership: a.ownership || 'owned',
          supplierId: a.supplierId ?? '', purchaseCost: a.purchaseCost ?? '', purchaseDate: a.purchaseDate || '',
          poNumber: a.poNumber || '', invoiceNumber: a.invoiceNumber || '', warrantyStart: a.warrantyStart || '',
          warrantyUntil: a.warrantyUntil || '', department: a.department || '', location: a.location || '',
          os: a.os || '', ram: a.ram || '', storage: a.storage || '', cpuSpeed: a.cpuSpeed || '',
          macAddress: a.macAddress || '', ipAddress: a.ipAddress || '', notes: a.notes || '',
          acquisitionType: a.specifications.acquisitionType || 'purchase',
          salvageValue: a.specifications.salvageValue ?? '', cpuCoreCount: a.specifications.cpuCoreCount ?? '',
        })))
        .catch((e) => setLoadError(e.message));
    } else {
      nextAssetCode().then((code) => setForm((prev) => (prev.asset_code ? prev : { ...prev, asset_code: code }))).catch(() => undefined);
    }
  }, [editId, fetchAsset, nextAssetCode]);

  const handlePhotosSelected = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    try {
      const processed = await processSelectedFiles(files, 'assets', `photos/${form.asset_code || 'TBA'}`, {
        assetId: editId || '',
        assetTag: form.asset_code,
        kind: 'photo',
      });
      const imageFiles = processed.filter(p => p.isImage);
      if (imageFiles.length === 0) {
        toast.error('Please select image files (PNG, JPG, WEBP)');
        return;
      }
      const updated = [...uploadedPhotos, ...imageFiles];
      setUploadedPhotos(updated);
      toast.success(`${imageFiles.length} photo(s) selected`);
    } catch (e) {
      toast.error('Failed to read image files');
    }
  };

  const handleRemovePhoto = (id: string) => {
    const updated = uploadedPhotos.filter(p => p.id !== id);
    setUploadedPhotos(updated);
  };

  const handleInvoiceFileSelected = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    try {
      const processed = await processSelectedFiles(files, 'assets', 'invoices', {
        assetId: editId || '',
        assetTag: form.asset_code,
        kind: 'invoice',
      });
      if (processed.length > 0) {
        const item = processed[0];
        setUploadedInvoiceDoc(item);
        toast.success(`Invoice file "${item.name}" loaded (${item.size})`);
      }
    } catch (e) {
      toast.error('Failed to read invoice file');
    }
  };

  const handleWarrantyFileSelected = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    try {
      const processed = await processSelectedFiles(files, 'assets', 'warranty', {
        assetId: editId || '',
        assetTag: form.asset_code,
        kind: 'warranty',
      });
      if (processed.length > 0) {
        const item = processed[0];
        setUploadedWarrantyDoc(item);
        toast.success(`Warranty file "${item.name}" loaded (${item.size})`);
      }
    } catch (e) {
      toast.error('Failed to read warranty file');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Not on the final step yet (e.g. Enter pressed in a field) — advance instead of saving.
    if (!isLastStep) {
      goToStep(currentStep + 1);
      return;
    }
    // Final step just appeared — ignore the trailing click of a rapid "Next, Next".
    if (!readyToSubmit) return;

    // Required fields: Name, Type, Category and Serial number (all on the General step).
    const missing: string[] = [];
    if (!String(form.name || '').trim()) missing.push('Asset Name');
    if (!String(form.type || '').trim()) missing.push('Asset Type');
    if (!String(form.category || '').trim()) missing.push('Category');
    if (!String(form.serial_number || '').trim()) missing.push('Serial Number');
    if (missing.length) {
      toast.error(`Please fill in: ${missing.join(', ')}`);
      goToStep(SECTIONS.indexOf('general'));
      return;
    }

    const resolvedName = String(form.name || '').trim();

    const specifications: Record<string, any> = {};
    const payload: Record<string, any> = {};
    for (const [key, value] of Object.entries(form)) {
      if (SPEC_FIELDS.includes(key)) {
        if (value !== '' && value !== null && value !== undefined) specifications[key] = value;
      } else {
        payload[key] = value === '' ? null : value;
      }
    }
    payload.name = resolvedName;
    payload.type = form.type || (types[0]?.name ?? 'Hardware');
    if (!payload.asset_code) delete payload.asset_code; // server generates it
    payload.supplierId = form.supplierId === '' ? null : Number(form.supplierId);
    payload.purchaseCost = form.purchaseCost === '' ? null : Number(form.purchaseCost);
    payload.specifications = specifications;

    setSaving(true);
    try {
      const saved = isEdit ? await updateAsset(editId!, payload) : await createAsset(payload);

      // Files were uploaded to storage when selected; record them against the asset.
      const pending: { kind: 'image' | 'invoice' | 'warranty'; item: UploadedFileItem; metadata?: Record<string, any> }[] = [
        ...uploadedPhotos.map((item) => ({ kind: 'image' as const, item })),
        ...(uploadedInvoiceDoc ? [{ kind: 'invoice' as const, item: uploadedInvoiceDoc, metadata: {
          invoice_number: form.invoiceNumber || null,
          amount: form.purchaseCost === '' ? null : Number(form.purchaseCost),
          vendor: vendors.find((v) => String(v.id) === String(form.supplierId))?.name || null,
          invoice_date: form.purchaseDate || null,
        } }] : []),
        ...(uploadedWarrantyDoc ? [{ kind: 'warranty' as const, item: uploadedWarrantyDoc, metadata: { title: `Warranty - ${uploadedWarrantyDoc.name}` } }] : []),
      ];
      const notUploaded = pending.filter((p) => !p.item.fileUrl || p.item.fileUrl.startsWith('data:'));
      for (const p of pending) {
        if (notUploaded.includes(p)) continue;
        await addAttachment(saved.id, {
          kind: p.kind, filename: p.item.name, path: p.item.fileUrl, mimeType: p.item.type, sizeBytes: p.item.sizeBytes, metadata: p.metadata,
        });
      }

      if (!isEdit && assignEmployeeId) {
        await assignAsset(saved.id, assignEmployeeId);
      }

      if (notUploaded.length) {
        toast.error(`${notUploaded.length} file(s) could not be uploaded to storage and were not attached`, { duration: 7000 });
      }
      toast.success(isEdit ? `${saved.asset_code} updated` : `${saved.asset_code} registered`);
      navigate(`/assets/registry/${saved.asset_code}`);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Categories shown for the chosen type: a category with no type (universal)
  // shows for every type; otherwise its type must match the selected type.
  // When no type is selected yet, show everything.
  const selectedTypeName = String(form.type || '');
  const categoriesForType = categories.filter(
    (c) => !c.typeName || !selectedTypeName || c.typeName.toLowerCase() === selectedTypeName.toLowerCase()
  );
  const currentCategoryInList = !form.category || categoriesForType.some((c) => c.name === form.category);

  // Changing the type drops a now-invalid category selection so it can't be
  // saved under a mismatched type; a universal category and a blank stay.
  const handleTypeChange = (newType: string) => {
    setForm((prev) => {
      const stillValid = !prev.category || categories.some(
        (c) => c.name === prev.category &&
          (!c.typeName || c.typeName.toLowerCase() === newType.toLowerCase())
      );
      return { ...prev, type: newType, category: stillValid ? prev.category : '' };
    });
  };

  if (loadError) {
    return (
      <div className="p-6">
        <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive-strong">{loadError}</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 bg-background min-h-full text-foreground text-xs font-sans">
      {/* HEADER CARD */}
      <div className="p-4 rounded-xl border border-border bg-card shadow-xs flex flex-col sm:flex-row justify-between sm:items-center gap-3 shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/assets/registry')}
            className="inline-flex size-9 items-center justify-center rounded-xl border border-border bg-background hover:bg-muted text-foreground transition-colors shrink-0 shadow-2xs"
            title="Back to Asset Management"
          >
            <ArrowLeft className="size-4" />
          </button>
          <div>
            <h1 className="text-foreground font-display text-page-title tracking-tight">
              {isEdit ? `Edit ${form.asset_code || 'asset'}` : 'Register new asset'}
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">Fill in whatever you know — nothing is required. Use Next to move through the sections.</p>
          </div>
        </div>
      </div>

      {/* SECTION TABS */}
      <div className="flex items-center gap-2 border-b border-border pb-2 overflow-x-auto">
        {[
          { id: 'general', label: '1. General & Type' },
          { id: 'purchase', label: '2. Purchase & Rent Info' },
          { id: 'assignment', label: '3. Assignment & Location' },
          { id: 'specs', label: '4. Device Specifications' },
          { id: 'media', label: '5. Invoices, Photos & S3' },
          { id: 'notes', label: '6. Notes & Remarks' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSection(tab.id as any)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
              activeSection === tab.id
                ? 'bg-primary text-white shadow-xs'
                : 'bg-muted/50 text-muted-foreground hover:bg-accent'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* FORM CONTENT */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* SECTION 1: GENERAL */}
        {activeSection === 'general' && (
          <div className="rounded-lg border border-border bg-card p-5 shadow-xs space-y-4">
            <h3 className="text-muted-foreground border-b border-border pb-2 font-display text-card-title">
              General Asset & Identification Info
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block font-semibold text-foreground mb-1">Asset code</label>
                <input
                  type="text"
                  placeholder="Generated automatically"
                  disabled={isEdit}
                  value={form.asset_code || ''}
                  onChange={(e) => setForm({ ...form, asset_code: e.target.value })}
                  className="w-full h-8 px-2.5 rounded-md border border-input bg-background font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                />
              </div>

              <div>
                <label className="block font-semibold text-foreground mb-1">Asset Name <span className="text-destructive">*</span></label>
                <input
                  type="text"
                  placeholder="e.g. MacBook Pro 16-inch M3 Max"
                  value={form.name || ''}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full h-8 px-2.5 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                />
              </div>

              <div>
                <label className="block font-semibold text-foreground mb-1">Asset Type <span className="text-destructive">*</span></label>
                <select
                  value={form.type || (types[0]?.name ?? '')}
                  onChange={(e) => handleTypeChange(e.target.value)}
                  className="w-full h-8 px-2 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                >
                  {types.map((t) => (
                    <option key={t.id} value={t.name}>{t.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block font-semibold text-foreground mb-1">Category <span className="text-destructive">*</span></label>
                <select
                  value={form.category || ''}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full h-8 px-2 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                >
                  <option value="">{categoriesForType.length === 0 ? 'No categories for this type' : 'Not set'}</option>
                  {/* Keep a category chosen earlier even if it belongs to another type (e.g. editing legacy data). */}
                  {form.category && !currentCategoryInList && (
                    <option value={form.category}>{form.category} (other type)</option>
                  )}
                  {categoriesForType.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
                {selectedTypeName && (
                  <p className="mt-1 text-[11px] text-muted-foreground">Showing categories for {selectedTypeName}.</p>
                )}
              </div>
              <div>
                <label className="block font-semibold text-foreground mb-1">Warranty start</label>
                <input type="date" value={form.warrantyStart || ''} onChange={(e) => setForm({ ...form, warrantyStart: e.target.value })}
                  className="w-full h-8 px-2.5 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring" />
              </div>
              <div>
                <label className="block font-semibold text-foreground mb-1">Warranty end</label>
                <input type="date" value={form.warrantyUntil || ''} onChange={(e) => setForm({ ...form, warrantyUntil: e.target.value })}
                  className="w-full h-8 px-2.5 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block font-semibold text-foreground mb-1">Brand / Manufacturer</label>
                <input
                  type="text"
                  placeholder="e.g. Apple Inc., Dell, Cisco"
                  value={form.manufacturer || ''}
                  onChange={(e) => setForm({ ...form, manufacturer: e.target.value })}
                  className="w-full h-8 px-2.5 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                />
              </div>

              <div>
                <label className="block font-semibold text-foreground mb-1">Model Number</label>
                <input
                  type="text"
                  placeholder="e.g. U3223QE, MX85-HW"
                  value={form.model || ''}
                  onChange={(e) => setForm({ ...form, model: e.target.value })}
                  className="w-full h-8 px-2.5 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                />
              </div>

              <div>
                <label className="block font-semibold text-foreground mb-1">Serial Number <span className="text-destructive">*</span></label>
                <input
                  type="text"
                  placeholder="e.g. C02GX981MD6R"
                  value={form.serial_number || ''}
                  onChange={(e) => setForm({ ...form, serial_number: e.target.value })}
                  className="w-full h-8 px-2.5 rounded-md border border-input bg-background font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                />
              </div>
            </div>
          </div>
        )}

        {/* SECTION 2: PURCHASE / RENT */}
        {activeSection === 'purchase' && (
          <div className="rounded-lg border border-border bg-card p-5 shadow-xs space-y-4">
            <h3 className="text-muted-foreground border-b border-border pb-2 font-display text-card-title">
              Purchase & Financial Logistics
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block font-semibold text-foreground mb-1">Ownership</label>
                <select
                  value={form.ownership || 'owned'}
                  onChange={(e) => setForm({ ...form, ownership: e.target.value })}
                  className="w-full h-8 px-2 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                >
                  <option value="owned">Owned</option>
                  <option value="rental">Rental</option>
                </select>
              </div>

              <div>
                <label className="block font-semibold text-foreground mb-1">Acquisition Type</label>
                <select
                  value={form.acquisitionType || 'purchase'}
                  onChange={(e) => setForm({ ...form, acquisitionType: e.target.value as any })}
                  className="w-full h-8 px-2 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                >
                  <option value="purchase">Purchase Info</option>
                  <option value="rent">Rent Info</option>
                </select>
              </div>

              <div>
                <label className="block font-semibold text-foreground mb-1">Vendor</label>
                <select
                  value={form.supplierId === null ? '' : String(form.supplierId ?? '')}
                  onChange={(e) => setForm({ ...form, supplierId: e.target.value })}
                  className="w-full h-8 px-2 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                >
                  <option value="">Not set</option>
                  {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>

              <div>
                <label className="block font-semibold text-foreground mb-1">Purchase cost</label>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.purchaseCost ?? ''}
                  onChange={(e) => setForm({ ...form, purchaseCost: e.target.value })}
                  className="w-full h-8 px-2.5 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block font-semibold text-foreground mb-1">Purchase Date</label>
                <input
                  type="date"
                  value={form.purchaseDate || ''}
                  onChange={(e) => setForm({ ...form, purchaseDate: e.target.value })}
                  className="w-full h-8 px-2.5 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                />
              </div>

              <div>
                <label className="block font-semibold text-foreground mb-1">PO Number</label>
                <input
                  type="text"
                  placeholder="PO-2025-001"
                  value={form.poNumber || ''}
                  onChange={(e) => setForm({ ...form, poNumber: e.target.value })}
                  className="w-full h-8 px-2.5 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                />
              </div>

              <div>
                <label className="block font-semibold text-foreground mb-1">Invoice Number</label>
                <input
                  type="text"
                  placeholder="INV-9981"
                  value={form.invoiceNumber || ''}
                  onChange={(e) => setForm({ ...form, invoiceNumber: e.target.value })}
                  className="w-full h-8 px-2.5 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                />
              </div>

              <div>
                <label className="block font-semibold text-foreground mb-1">Salvage value</label>
                <input
                  type="number"
                  min={0}
                  value={form.salvageValue ?? ''}
                  onChange={(e) => setForm({ ...form, salvageValue: e.target.value })}
                  className="w-full h-8 px-2.5 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                />
              </div>
            </div>
          </div>
        )}

        {/* SECTION 3: ASSIGNMENT */}
        {activeSection === 'assignment' && (
          <div className="rounded-lg border border-border bg-card p-5 shadow-xs space-y-4">
            <h3 className="text-muted-foreground border-b border-border pb-2 font-display text-card-title">
              Assignment & Location Details
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block font-semibold text-foreground mb-1">Assign to</label>
                {isEdit ? (
                  <p className="h-8 text-xs leading-8 text-muted-foreground">Use Check out / Check in on the asset page.</p>
                ) : (
                  <select
                    value={assignEmployeeId}
                    onChange={(e) => {
                      const emp = employees.find((x) => x.id === e.target.value);
                      setAssignEmployeeId(e.target.value);
                      if (emp && !form.department) setForm({ ...form, department: emp.department });
                    }}
                    className="w-full h-8 px-2 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  >
                    <option value="">Keep in stock</option>
                    {employees.map((e) => <option key={e.id} value={e.id}>{e.name}{e.code ? ` (${e.code})` : ''}{e.department ? ` · ${e.department}` : ''}</option>)}
                  </select>
                )}
                {!isEdit && employees.find((x) => x.id === assignEmployeeId) && (
                  <EmployeeCard className="mt-2" employee={employees.find((x) => x.id === assignEmployeeId)!} showProfileLink={false} />
                )}
              </div>

              <div>
                <label className="block font-semibold text-foreground mb-1">Department</label>
                <input
                  type="text"
                  placeholder="e.g. Engineering"
                  value={form.department || ''}
                  onChange={(e) => setForm({ ...form, department: e.target.value })}
                  className="w-full h-8 px-2.5 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                />
              </div>

              <div>
                <label className="block font-semibold text-foreground mb-1">Location</label>
                <input
                  type="text"
                  placeholder="Building A - Floor 3"
                  value={form.location || ''}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  className="w-full h-8 px-2.5 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                />
              </div>
            </div>
          </div>
        )}

        {/* SECTION 4: DEVICE SPECS */}
        {activeSection === 'specs' && (
          <div className="rounded-lg border border-border bg-card p-5 shadow-xs space-y-4">
            <h3 className="text-muted-foreground border-b border-border pb-2 font-display text-card-title">
              Hardware Device Specifications
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block font-semibold text-foreground mb-1">OS Installed</label>
                <input
                  type="text"
                  placeholder="e.g. macOS"
                  value={form.os || ''}
                  onChange={(e) => setForm({ ...form, os: e.target.value })}
                  className="w-full h-8 px-2.5 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                />
              </div>

              <div>
                <label className="block font-semibold text-foreground mb-1">Memory (RAM GB)</label>
                <input
                  type="text"
                  placeholder="36"
                  value={form.ram || ''}
                  onChange={(e) => setForm({ ...form, ram: e.target.value })}
                  className="w-full h-8 px-2.5 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                />
              </div>

              <div>
                <label className="block font-semibold text-foreground mb-1">Disk Space (Storage GB)</label>
                <input
                  type="text"
                  placeholder="1000"
                  value={form.storage || ''}
                  onChange={(e) => setForm({ ...form, storage: e.target.value })}
                  className="w-full h-8 px-2.5 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                />
              </div>

              <div>
                <label className="block font-semibold text-foreground mb-1">CPU Cores</label>
                <input
                  type="text"
                  placeholder="16"
                  value={form.cpuCoreCount || ''}
                  onChange={(e) => setForm({ ...form, cpuCoreCount: e.target.value })}
                  className="w-full h-8 px-2.5 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                />
              </div>
            </div>
          </div>
        )}

        {/* SECTION 5: INVOICES, PHOTOS & S3 ATTACHMENTS */}
        {activeSection === 'media' && (
          <div className="rounded-lg border border-border bg-card p-5 shadow-xs space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border pb-3">
              <div>
                <h3 className="text-foreground font-display text-card-title">
                  Invoices, Hardware Photos &amp; AWS S3 Storage
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Upload hardware condition photos, vendor tax invoices, and warranty certificates directly from your computer.
                </p>
              </div>

              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-success/10 border border-success/20 text-success font-mono text-xs font-semibold">
                <span className="size-2 rounded-full bg-success animate-pulse"></span>
                Files upload to the configured asset storage
              </div>
            </div>

            {/* 1. ASSET PHOTOS GALLERY DROPZONE */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-xs font-semibold text-foreground">
                  1. Hardware Condition Photos &amp; Barcode Scans ({uploadedPhotos.length} Added)
                </label>
                {uploadedPhotos.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setUploadedPhotos([]);
                      setForm(prev => ({ ...prev, images: [] }));
                    }}
                    className="text-destructive hover:underline text-xs font-semibold"
                  >
                    Clear All Photos
                  </button>
                )}
              </div>

              {/* Drag and Drop Zone */}
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDraggingPhotos(true);
                }}
                onDragLeave={() => setIsDraggingPhotos(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDraggingPhotos(false);
                  handlePhotosSelected(e.dataTransfer.files);
                }}
                onClick={() => photoFileInputRef.current?.click()}
                className={cn(
                  'border-2 border-dashed rounded-lg p-5 text-center cursor-pointer transition-all flex flex-col items-center justify-center gap-2',
                  isDraggingPhotos ? 'border-primary bg-primary/5' : 'border-border bg-muted/20 hover:border-primary/50 hover:bg-muted/30'
                )}
              >
                <input
                  ref={photoFileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => handlePhotosSelected(e.target.files)}
                />
                <div className="size-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                  <UploadCloud className="size-5" />
                </div>
                <div>
                  <p className="font-semibold text-foreground text-xs">Click to browse or drop hardware photos here</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Supports PNG, JPG, JPEG, WEBP</p>
                </div>
              </div>

              {/* Photo Previews */}
              {uploadedPhotos.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2.5 pt-1">
                  {uploadedPhotos.map((photo, idx) => (
                    <div key={photo.id} className="relative aspect-video rounded-lg overflow-hidden border border-border group bg-muted">
                      <img src={photo.dataUrl} alt={photo.name} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemovePhoto(photo.id);
                        }}
                        className="absolute top-1 right-1 size-5 rounded-full bg-black/80 text-white flex items-center justify-center hover:bg-destructive/90 transition-colors"
                        title="Remove photo"
                      >
                        <X className="size-3" />
                      </button>
                      <span className="absolute bottom-1 left-1 bg-black/75 text-white px-1 py-0.5 text-xs font-mono rounded max-w-[80%] truncate">
                        #{idx + 1} · {photo.size}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 2. PURCHASE INVOICE DOCUMENT UPLOAD */}
            <div className="space-y-3 pt-4 border-t border-border">
              <label className="block text-xs font-semibold text-foreground">
                2. Vendor Purchase Invoice Document (.pdf, .docx, .png, .jpg)
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">Invoice Number</label>
                  <input
                    type="text"
                    placeholder="e.g. INV-2026-9921"
                    value={form.invoiceNumber || ''}
                    onChange={(e) => setForm({ ...form, invoiceNumber: e.target.value })}
                    className="w-full h-8 px-2.5 rounded-md border border-input bg-background font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">PO Reference</label>
                  <input
                    type="text"
                    placeholder="e.g. PO-8871"
                    value={form.poNumber || ''}
                    onChange={(e) => setForm({ ...form, poNumber: e.target.value })}
                    className="w-full h-8 px-2.5 rounded-md border border-input bg-background font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">Invoice Amount ($)</label>
                  <input
                    type="number"
                    placeholder="1200"
                    value={form.purchaseCost ?? ''}
                    onChange={(e) => setForm({ ...form, cost: Number(e.target.value), purchaseCost: Number(e.target.value) })}
                    className="w-full h-8 px-2.5 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
              </div>

              {/* Invoice File Dropzone or Attached Card */}
              {uploadedInvoiceDoc ? (
                <div className="p-3.5 rounded-lg border border-border bg-background flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="size-9 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
                      <FileText className="size-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-xs text-foreground">{uploadedInvoiceDoc.name}</span>
                        <span className="px-2 py-0.5 rounded bg-success/10 text-success font-mono text-xs font-semibold">
                          {uploadedInvoiceDoc.size}
                        </span>
                      </div>
                      <p className="text-xs font-mono text-muted-foreground">
                        {uploadedInvoiceDoc.fileUrl && !uploadedInvoiceDoc.fileUrl.startsWith('data:') ? 'Uploaded — attached when you save' : 'Upload failed — this file will not be attached'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => invoiceFileInputRef.current?.click()}
                      className="px-2.5 py-1 text-xs rounded border border-border bg-card hover:bg-accent font-semibold text-foreground"
                    >
                      Replace File
                    </button>
                    <button
                      type="button"
                      onClick={() => setUploadedInvoiceDoc(null)}
                      className="p-1 rounded text-muted-foreground hover:text-destructive"
                      title="Remove document"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDraggingInvoice(true);
                  }}
                  onDragLeave={() => setIsDraggingInvoice(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDraggingInvoice(false);
                    handleInvoiceFileSelected(e.dataTransfer.files);
                  }}
                  onClick={() => invoiceFileInputRef.current?.click()}
                  className={cn(
                    'border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all flex items-center justify-center gap-3',
                    isDraggingInvoice ? 'border-primary bg-primary/5' : 'border-border bg-muted/20 hover:border-primary/50 hover:bg-muted/30'
                  )}
                >
                  <input
                    ref={invoiceFileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                    className="hidden"
                    onChange={(e) => handleInvoiceFileSelected(e.target.files)}
                  />
                  <FileUp className="size-5 text-primary" />
                  <div className="text-left">
                    <p className="font-semibold text-foreground text-xs">Click to browse or drop invoice file (.pdf, .docx, .png)</p>
                    <p className="text-xs text-muted-foreground">Files are linked directly to this asset record and cloud S3 bucket</p>
                  </div>
                </div>
              )}
            </div>

            {/* 3. WARRANTY & HANDOVER ATTACHMENTS */}
            <div className="space-y-3 pt-4 border-t border-border">
              <label className="block text-xs font-semibold text-foreground">
                3. Warranty Certificate &amp; Handover Sign-Off (.pdf, .docx, .png)
              </label>

              {uploadedWarrantyDoc ? (
                <div className="p-3.5 rounded-lg border border-border bg-background flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="size-9 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
                      <ShieldCheck className="size-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-xs text-foreground">{uploadedWarrantyDoc.name}</span>
                        <span className="px-2 py-0.5 rounded bg-primary/10 text-primary font-mono text-xs font-semibold">
                          {uploadedWarrantyDoc.size}
                        </span>
                      </div>
                      <p className="text-xs font-mono text-muted-foreground">
                        {uploadedWarrantyDoc.fileUrl && !uploadedWarrantyDoc.fileUrl.startsWith('data:') ? 'Uploaded — attached when you save' : 'Upload failed — this file will not be attached'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => warrantyFileInputRef.current?.click()}
                      className="px-2.5 py-1 text-xs rounded border border-border bg-card hover:bg-accent font-semibold text-foreground"
                    >
                      Replace File
                    </button>
                    <button
                      type="button"
                      onClick={() => setUploadedWarrantyDoc(null)}
                      className="p-1 rounded text-muted-foreground hover:text-destructive"
                      title="Remove certificate"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDraggingWarranty(true);
                  }}
                  onDragLeave={() => setIsDraggingWarranty(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDraggingWarranty(false);
                    handleWarrantyFileSelected(e.dataTransfer.files);
                  }}
                  onClick={() => warrantyFileInputRef.current?.click()}
                  className={cn(
                    'border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all flex items-center justify-center gap-3',
                    isDraggingWarranty ? 'border-primary bg-primary/5' : 'border-border bg-muted/20 hover:border-primary/50 hover:bg-muted/30'
                  )}
                >
                  <input
                    ref={warrantyFileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                    className="hidden"
                    onChange={(e) => handleWarrantyFileSelected(e.target.files)}
                  />
                  <ShieldCheck className="size-5 text-primary" />
                  <div className="text-left">
                    <p className="font-semibold text-foreground text-xs">Click to browse or drop warranty / sign-off certificate</p>
                    <p className="text-xs text-muted-foreground">Validated and persisted with asset warranty dates</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* SECTION 6: NOTES & REMARKS */}
        {activeSection === 'notes' && (
          <div className="rounded-lg border border-border bg-card p-5 shadow-xs space-y-4">
            <h3 className="text-muted-foreground border-b border-border pb-2 font-display text-card-title">
              Notes &amp; Additional Remarks
            </h3>

            <div>
              <label className="block text-xs font-semibold text-foreground mb-1">Inspection Notes &amp; Additional Remarks</label>
              <textarea
                rows={5}
                placeholder="Enter any additional logistics notes, condition remarks, or serial observations..."
                value={form.notes || ''}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full p-3 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
          </div>
        )}

        {/* STEP NAVIGATION */}
        <div className="flex items-center justify-between gap-3 pt-4 border-t border-border">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/assets/registry')}
              className="rounded-lg border border-border px-4 py-2 text-xs font-semibold text-foreground hover:bg-accent"
            >
              Cancel
            </button>
            <span className="text-xs text-muted-foreground">Step {currentStep + 1} of {SECTIONS.length}</span>
          </div>

          <div className="flex items-center gap-3">
            {currentStep > 0 && (
              <button
                type="button"
                onClick={() => goToStep(currentStep - 1)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border px-4 py-2 text-xs font-semibold text-foreground hover:bg-accent"
              >
                <ArrowLeft className="size-4" /> Back
              </button>
            )}

            {!isLastStep ? (
              <button
                type="button"
                onClick={() => goToStep(currentStep + 1)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-5 py-2 text-xs font-semibold text-white shadow hover:bg-primary/90"
              >
                Next <ArrowRight className="size-4" />
              </button>
            ) : (
              <button
                type="submit"
                disabled={saving || !readyToSubmit}
                title={!readyToSubmit ? 'Review the details, then register' : undefined}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-5 py-2 text-xs font-semibold text-white shadow hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Plus className="size-4" /> {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Register asset'}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
